/**
 * The lesson widget registry (`LN-SEED01`, STYLE_GUIDE.md §4).
 *
 * A widget is authored as a sanitizer-safe block:
 *
 *   <div class="pc-widget" data-widget="loop-trace" data-rows="[[1,1],[2,3]]" …>
 *     <p class="pc-widget-fallback">i = 1 gives sum 1, i = 2 gives 3.</p>
 *   </div>
 *
 * The data lives in `data-*` attributes; the children are the fallback a
 * reader sees with no JavaScript, in the admin preview, and in any other
 * renderer of this HTML. `hydrateWidgets` replaces that fallback with the
 * widget's first frame, built from the same data, and adds the controls.
 *
 * Three rules every widget here follows:
 *   1. the first frame is static, and it is the frame the fallback describes;
 *   2. there is no autoplay and no timer, so a widget is as still under
 *      `prefers-reduced-motion` as a diagram (the only motion is a CSS
 *      transition, and `progsity-content.css` gates those);
 *   3. data that does not parse leaves the fallback alone. A broken attribute
 *      never blanks a paragraph of the lesson.
 *
 * A widget is used only where the thing being explained moves
 * (STYLE_GUIDE.md §4); a picture that holds still is an inline `<svg>`.
 */

import { renderArrayIndex } from "./array-index";
import { renderCallStack } from "./call-stack";
import { renderCoercionTable } from "./coercion-table";
import { renderDequeBlocks } from "./deque-blocks";
import type { WidgetText } from "./dom";
import { renderEnginePipeline } from "./engine-pipeline";
import { renderIteratorWalk } from "./iterator-walk";
import { renderLoopTrace } from "./loop-trace";
import { renderMemoryBox } from "./memory-box";
import { renderPointerArrow } from "./pointer-arrow";
import { renderStringBytes } from "./string-bytes";
import { renderStringIndex } from "./string-index";
import { renderTruthTable } from "./truth-table";
import { renderValueBox } from "./value-box";
import { renderVectorGrow } from "./vector-grow";

export type { WidgetText } from "./dom";

/** Returns false when the block's data cannot be read; the fallback stays. */
type WidgetRenderer = (host: HTMLElement, text: WidgetText) => boolean;

export const WIDGETS: Record<string, WidgetRenderer> = {
  "memory-box": renderMemoryBox,
  "loop-trace": renderLoopTrace,
  "call-stack": renderCallStack,
  "array-index": renderArrayIndex,
  "pointer-arrow": renderPointerArrow,
  "truth-table": renderTruthTable,
  // `LN-WGT02`, the STL track.
  "iterator-walk": renderIteratorWalk,
  "vector-grow": renderVectorGrow,
  "string-bytes": renderStringBytes,
  "deque-blocks": renderDequeBlocks,
  // `LN-WGT03`, the JavaScript track.
  "engine-pipeline": renderEnginePipeline,
  "value-box": renderValueBox,
  "coercion-table": renderCoercionTable,
  "string-index": renderStringIndex,
};

/**
 * Hydrate every `div[data-widget]` under `root`, the widgets' own words from
 * `text` (the `lessons.widgets` messages, so `/bn` reads them in Bangla).
 *
 * Idempotent, guarded by `data-pc-widget-state`, because React can recreate
 * the `dangerouslySetInnerHTML` subtree after the effect has run: the same
 * reason `enhanceCodeBlocks` is idempotent.
 */
export function hydrateWidgets(root: HTMLElement, text: WidgetText): void {
  root.querySelectorAll<HTMLElement>("[data-widget]").forEach((host) => {
    if (host.dataset.pcWidgetState) return;
    const render = WIDGETS[host.dataset.widget ?? ""];
    if (!render) {
      host.dataset.pcWidgetState = "unknown";
      return;
    }
    // Kept so a widget that throws half way through can put the authored
    // fallback back: a broken widget must never cost the reader a paragraph.
    const fallback = Array.from(host.childNodes).map((node) => node.cloneNode(true));
    try {
      host.dataset.pcWidgetState = render(host, text) ? "ready" : "fallback";
    } catch {
      host.textContent = "";
      host.append(...fallback);
      host.dataset.pcWidgetState = "fallback";
    }
  });
}
